package com.trevorwiebe.timesheet.settlements.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import com.trevorwiebe.timesheet.core.presentation.common.rememberLinkOpener
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.settlements.domain.model.Settlement
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun SettlementsScreen(
    onBack: (() -> Unit)?,
    viewModel: SettlementsViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsState()
    val onEvent = viewModel::onEvent
    val openPdf = rememberLinkOpener(viewModel.state, { it.pdfUrlToOpen }, { it.isGeneratingPdf }) {
        onEvent(SettlementsEvent.OnPdfOpened)
    }
    val selected = state.selectedSettlement

    when {
        state.newSettlement != null -> NewSettlementContent(state = state, onEvent = onEvent)
        selected != null -> SettlementDetailContent(settlement = selected, state = state, openPdf = openPdf, onEvent = onEvent)
        else -> SettlementListContent(state = state, onBack = onBack, onEvent = onEvent)
    }

    NativeDialog(
        visible = state.error != null,
        title = "Something went wrong",
        message = state.error,
        confirmText = "OK",
        onConfirm = { onEvent(SettlementsEvent.OnErrorShown) },
        onDismiss = { onEvent(SettlementsEvent.OnErrorShown) },
    )
}

@Composable
private fun SettlementListContent(
    state: SettlementsState,
    onBack: (() -> Unit)?,
    onEvent: (SettlementsEvent) -> Unit,
) {
    Scaffold(
        topBar = {
            TopBar(
                title = "Settlements",
                backIcon = if (onBack != null) ({ BackIcon() }) else null,
                onBack = { onBack?.invoke() },
                actions = {
                    TextButton(onClick = { onEvent(SettlementsEvent.OnNewSettlementClick) }) {
                        Text("New settlement", color = MaterialTheme.colorScheme.tertiary)
                    }
                },
            )
        }
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
            Column(Modifier.fillMaxSize()) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 4.dp),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    SettlementListTab.entries.forEach { tab ->
                        val isSelected = tab == state.tab
                        FilterChip(
                            selected = isSelected,
                            onClick = { onEvent(SettlementsEvent.OnSelectTab(tab)) },
                            label = { Text(tab.label) },
                            border = FilterChipDefaults.filterChipBorder(
                                enabled = true,
                                borderColor = MaterialTheme.colorScheme.tertiary,
                                selected = isSelected,
                            ),
                            colors = FilterChipDefaults.filterChipColors(
                                containerColor = MaterialTheme.colorScheme.background,
                                selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                                selectedLabelColor = MaterialTheme.colorScheme.onTertiary,
                            ),
                        )
                    }
                }
                HorizontalDivider()
                when {
                    state.isLoading -> Box(Modifier.fillMaxSize()) {
                        CircularProgressIndicator(
                            modifier = Modifier.align(Alignment.Center),
                            color = MaterialTheme.colorScheme.tertiary,
                        )
                    }
                    state.visibleSettlements.isEmpty() -> Text(
                        text = when (state.tab) {
                            SettlementListTab.DRAFTS -> "No draft settlements. Use New settlement to pay an owner operator or carrier."
                            SettlementListTab.SENT -> "No sent settlements yet."
                        },
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 32.dp),
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                    )
                    else -> LazyColumn(Modifier.fillMaxSize()) {
                        items(state.visibleSettlements, key = { it.id }) { settlement ->
                            SettlementRow(
                                settlement = settlement,
                                onClick = { onEvent(SettlementsEvent.OnSettlementClick(settlement.id)) },
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SettlementRow(settlement: Settlement, onClick: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 8.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                text = "${settlement.numberLabel}  ${settlement.ownerOperatorName}",
                fontSize = 14.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f),
            )
            Text(text = settlement.netPayFormatted, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            val count = settlement.lines.size
            Text(
                text = "${periodLabel(settlement)} • $count load${if (count == 1) "" else "s"}",
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                modifier = Modifier.weight(1f),
            )
            Text(text = "Net pay", fontSize = 13.sp, color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f))
        }
    }
    HorizontalDivider()
}

internal fun periodLabel(settlement: Settlement): String {
    val start = settlement.periodStart ?: return ""
    val end = settlement.periodEnd ?: return ""
    return if (start == end) Util.toFriendlyDate(start) else "${Util.toFriendlyDate(start)} – ${Util.toFriendlyDate(end)}"
}
